import { useMemo, useState } from 'react';
import {
  Description,
  Drawer,
  Label,
  ListBox,
  Modal,
  SearchField,
  useMediaQuery
} from '@heroui/react';
import { useTranslation } from '@rhino-project/core/i18n';
import { getLanguageName } from '../../utils/language';

export interface LanguagePickerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const searchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const LanguageOptions = ({
  autoFocus,
  onSelected
}: {
  autoFocus: boolean;
  onSelected: () => void;
}) => {
  const {
    pickerLocale: locale,
    pickerLocales: availableLocales,
    setLocale,
    t
  } = useTranslation();
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const languages = useMemo(
    () =>
      availableLocales.map((code) => ({
        code,
        name: getLanguageName(code),
        translatedName: getLanguageName(code, locale)
      })),
    [availableLocales, locale]
  );
  const needle = searchText(query.trim().replaceAll('_', '-'));
  const matches = languages.filter(({ code, name, translatedName }) =>
    searchText(`${code} ${name} ${translatedName}`).includes(needle)
  );

  const changeLanguage = async (value: string) => {
    if (pending) return;
    setPending(true);
    setFailed(false);
    try {
      await setLocale(value);
      onSelected();
    } catch {
      setFailed(true);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <SearchField
        aria-label={t('rhino.frontend.search_languages')}
        value={query}
        onChange={setQuery}
      >
        <SearchField.Group>
          <SearchField.SearchIcon />
          <SearchField.Input
            autoFocus={autoFocus}
            placeholder={t('rhino.frontend.search_languages')}
          />
          <SearchField.ClearButton
            aria-label={t('rhino.frontend.messages.clear')}
          />
        </SearchField.Group>
      </SearchField>
      <ListBox
        aria-label={t('rhino.frontend.language')}
        aria-busy={pending}
        className="max-h-72 overflow-y-auto"
        selectionMode="single"
        selectionBehavior="toggle"
        shouldSelectOnPressUp
        selectedKeys={[locale]}
        disabledKeys={pending ? availableLocales : []}
        onSelectionChange={(keys) => {
          if (keys !== 'all') {
            // Selecting the current language also saves an explicit preference.
            void changeLanguage(String([...keys][0] ?? locale));
          }
        }}
        renderEmptyState={() => (
          <p className="p-3 text-sm text-muted">
            {t('rhino.frontend.no_languages_found')}
          </p>
        )}
      >
        {matches.map(({ code, name, translatedName }) => (
          <ListBox.Item key={code} id={code} textValue={name} aria-label={name}>
            <div className="flex flex-col">
              <Label lang={code}>{name}</Label>
              {translatedName !== name && (
                <Description>{translatedName}</Description>
              )}
            </div>
            <ListBox.ItemIndicator />
          </ListBox.Item>
        ))}
      </ListBox>
      {failed && (
        <p role="alert" className="text-sm text-danger">
          {t('rhino.frontend.messages.language_load_error')}
        </p>
      )}
    </div>
  );
};

export const LanguagePicker = ({
  isOpen,
  onOpenChange
}: LanguagePickerProps) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const options = (
    <LanguageOptions
      autoFocus={isDesktop}
      onSelected={() => onOpenChange(false)}
    />
  );

  if (isDesktop) {
    return (
      <Modal.Backdrop isOpen={isOpen} onOpenChange={onOpenChange}>
        <Modal.Container size="sm" placement="center">
          <Modal.Dialog>
            <Modal.CloseTrigger
              aria-label={t('rhino.frontend.messages.cancel')}
            />
            <Modal.Header>
              <Modal.Heading>{t('rhino.frontend.language')}</Modal.Heading>
            </Modal.Header>
            <Modal.Body>{options}</Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    );
  }

  return (
    <Drawer.Backdrop isOpen={isOpen} onOpenChange={onOpenChange}>
      <Drawer.Content placement="bottom">
        <Drawer.Dialog>
          <Drawer.Handle />
          <Drawer.CloseTrigger
            aria-label={t('rhino.frontend.messages.cancel')}
          />
          <Drawer.Header>
            <Drawer.Heading>{t('rhino.frontend.language')}</Drawer.Heading>
          </Drawer.Header>
          <Drawer.Body>{options}</Drawer.Body>
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  );
};
